import { useSocket } from '@context/SocketContext'
import { useTripRoom } from '@hooks/useTripRoom'
import { useEffect, useRef } from 'react'

/**
 * Calls `onUpdate` whenever the trip's Chat changes — a Message created, deleted or
 * reacted to, a preview or an Idea added — and after a reconnect, since events may have
 * been missed meanwhile. Events only say *what* changed; the caller refetches, following
 * the trip room's "go refetch" convention. Holds the trip's room while mounted.
 */
export const useChatUpdates = (tripId: string | undefined, onUpdate: () => void): void => {
	const { socket } = useSocket()
	const latest = useRef(onUpdate)
	latest.current = onUpdate
	useTripRoom(tripId)

	useEffect(() => {
		if (!socket || !tripId) return
		const handleUpdate = (data: { tripId?: string } | undefined) => {
			if (data?.tripId === tripId) latest.current()
		}
		const handleReconnect = () => latest.current()
		socket.on('chat_updated', handleUpdate)
		socket.io.on('reconnect', handleReconnect)
		return () => {
			socket.off('chat_updated', handleUpdate)
			socket.io.off('reconnect', handleReconnect)
		}
	}, [socket, tripId])
}
